import { useMemo } from "react";
import { useApiRequest } from "common/utils/api";
import { REQUEST_METHOD } from "common/enum/RequestMethod";
import { formatDateTime, getMonthRange } from "common/utils/dateUtil";
import {
  PaymentCategory,
  PaymentChannel,
  PaymentMethod,
  PaymentStatus,
} from "common/enum/PaymentTypes";

const weekdayKeys = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const weekdayMap = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const toPieData = (list, groupFn, labelMap) => {
  const grouped = Object.fromEntries(
    Object.keys(labelMap)
      .filter((key) => key !== "ALL")
      .map((key) => [key, 0]),
  );
  list.forEach((item) => {
    const key = groupFn(item);
    if (key === "ALL") return;
    grouped[key] = (grouped[key] || 0) + 1;
  });
  const data = Object.entries(grouped).map(([key, value]) => ({
    key,
    name: labelMap[key] ?? key,
    value,
  }));
  return { max: data.reduce((s, d) => s + d.value, 0), data };
};

export const useSalesChartData = (startDate, endDate) => {
  const clubId = sessionStorage.getItem("clubId");

  const { data: rawData, refetch: refetchPayments } = useApiRequest({
    method: REQUEST_METHOD.GET,
    url: `/api/v2.0/payment`,
    queryParameter: {
      clubId,
      startDate,
      endDate,
      listNum: Number.MAX_SAFE_INTEGER,
    },
  });

  const salesListData = useMemo(
    () =>
      rawData?.payments?.list?.filter((item) => item?.status === "APPROVED") ??
      [],
    [rawData],
  );
  const currency = rawData?.payments?.list[0]?.currency ?? "-";

  const totalAmount = useMemo(
    () =>
      salesListData
        .reduce((sum, item) => sum + Number(item?.totalAmount ?? 0), 0)
        .toFixed(2),
    [salesListData],
  );

  const transactionCount = salesListData.length;

  const averageAmount = useMemo(
    () =>
      transactionCount > 0
        ? (Number(totalAmount) / transactionCount).toFixed(2)
        : "0.00",
    [totalAmount, transactionCount],
  );

  const revenueShare = useMemo(() => {
    const grouped = Object.fromEntries(
      Object.keys(PaymentCategory)
        .filter((key) => key !== "ALL")
        .map((key) => [key, 0]),
    );
    salesListData.forEach((item) => {
      const key = item.category ?? "OTHER";
      if (key === "ALL") return;
      grouped[key] = (grouped[key] || 0) + Number(item.totalAmount ?? 0);
    });
    const data = Object.entries(grouped).map(([key, value]) => ({
      key,
      name: PaymentCategory[key] ?? key,
      value,
    }));
    return { max: data.reduce((s, d) => s + d.value, 0), data };
  }, [salesListData]);

  const onOffRate = useMemo(
    () =>
      toPieData(
        salesListData,
        (item) => item.paymentPath ?? "UNKNOWN",
        PaymentChannel,
      ),
    [salesListData],
  );

  const paymentMethod = useMemo(
    () =>
      toPieData(
        salesListData,
        (item) => item.paymentMethod ?? "UNKNOWN",
        PaymentMethod,
      ),
    [salesListData],
  );

  const barChartData = useMemo(() => {
    const weekdayGrouped = Object.fromEntries(weekdayKeys.map((d) => [d, 0]));
    salesListData.forEach((item) => {
      if (!item.paymentAt) return;
      const day = weekdayMap[new Date(item.paymentAt).getDay()];
      weekdayGrouped[day] += Number(item.totalAmount ?? 0);
    });
    return weekdayKeys.map((day) => ({ key: day, value: weekdayGrouped[day] }));
  }, [salesListData]);

  const areaChartData = useMemo(() => {
    const dateGrouped = salesListData.reduce((acc, item) => {
      if (!item.paymentAt) return acc;
      const key = item.paymentAt.slice(0, 10);
      acc[key] = (acc[key] || 0) + Number(item.totalAmount ?? 0);
      return acc;
    }, {});
    return [
      {
        id: "Revenue",
        data: Object.entries(dateGrouped)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([key, value]) => ({ key, value })),
      },
    ];
  }, [salesListData]);

  const refetchChart = (date) => {
    const { firstDay, lastDay } = getMonthRange(date);
    const params = {
      clubId,
      startDate: formatDateTime(firstDay, "YYYY-MM-DD"),
      endDate: formatDateTime(lastDay, "YYYY-MM-DD"),
    };
    refetchPayments({
      overrideParams: { ...params, listNum: Number.MAX_SAFE_INTEGER },
    });
  };

  return {
    currency,
    totalAmount,
    transactionCount,
    averageAmount,
    revenueShare,
    onOffRate,
    paymentMethod,
    barChartData,
    areaChartData,
    refetchChart,
  };
};
